import type { CreateWorkshopHolidayInput, WorkshopHoliday } from "./types";

const dayMs = 86400000;
export const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
export const dateKey = (date: Date) => date.toISOString().slice(0, 10);
export const todayInSeoul = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(new Date());
export const validDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && dateKey(new Date(value)) === value;
export const normalizeDates = (dates: readonly string[]) => [...new Set(dates.filter(validDate))].sort();
export const shiftDate = (date: string, days: number) => {
  const value = new Date(Date.parse(date) + days * dayMs);
  return Number.isFinite(+value) && value.getUTCFullYear() >= 1 && value.getUTCFullYear() <= 9999 ? dateKey(value) : date;
};
export const shiftMonth = (month: string, amount: number) => {
  const value = new Date(`${month}-01T00:00:00Z`); value.setUTCMonth(value.getUTCMonth() + amount);
  return Number.isFinite(+value) && value.getUTCFullYear() >= 1 && value.getUTCFullYear() <= 9999 ? dateKey(value).slice(0, 7) : month;
};
export const monthLabel = (month: string) => new Intl.DateTimeFormat("ko-KR", { year: "numeric", month: "long", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00Z`));
export const dateLabel = (date: string) => new Intl.DateTimeFormat("ko-KR", { month: "long", day: "numeric", weekday: "short", timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));
export const holidayTimeLabel = (startTime: string, endTime: string) =>
  startTime === "00:00" && endTime === "24:00"
    ? "종일"
    : `${Number(startTime.slice(0, 2))}시${startTime.slice(3) === "00" ? "" : ` ${startTime.slice(3)}분`}–${Number(endTime.slice(0, 2))}시${endTime.slice(3) === "00" ? "" : ` ${endTime.slice(3)}분`}`;
export function calendarCells(month: string) {
  const first = `${month}-01`, start = shiftDate(first, -new Date(first).getUTCDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = shiftDate(start, index);
    return { date, day: Number(date.slice(8)), weekday: index % 7, inMonth: date.startsWith(month) };
  });
}
/** Bound allocation to the server's 366-date contract, including leap years. */
export function datesInRange(first: string, last: string): string[] | null {
  if (!validDate(first) || !validDate(last)) return null;
  const [start, end] = first <= last ? [first, last] : [last, first];
  const length = Math.round((Date.parse(end) - Date.parse(start)) / dayMs) + 1;
  return length <= 366 ? Array.from({ length }, (_, i) => shiftDate(start, i)) : null;
}
export function dateGroups(values: readonly string[]) {
  const groups: Array<{ start: string; end: string }> = [];
  for (const date of normalizeDates(values)) {
    const previous = groups.at(-1);
    if (previous && shiftDate(previous.end, 1) === date) previous.end = date;
    else groups.push({ start: date, end: date });
  }
  return groups;
}
export const holidayDraft = (item: WorkshopHoliday): CreateWorkshopHolidayInput => ({ dates: normalizeDates(item.dates), startTime: item.startTime, endTime: item.endTime, name: item.name, createdBy: item.createdBy });
export const holidayIdentity = (item?: WorkshopHoliday) => item ? JSON.stringify({ ...holidayDraft(item), id: item.id }) : "missing";
